import { isEmpty as _isEmpty, parseInt } from 'lodash';
import { CUSTOM_IDENTITY_SYNC } from './identity-sync-card/components/consts';

/**
 *
 * Assemble the final analytics builder settings for request to API endpoint
 *
 * @param {{ writekey, pipelines, author }}
 *
 * @returns {object}
 */
const CROSS_DOMAIN_OPTION = 'crossDomain';
const CROSS_DOMAIN_DIRECT_OPTION = 'crossDomainDirect';
const DEFAULT_CROSS_DOMAIN_TIMEOUT = 1000;

function isCrossDomainSync(syncName) {
  return syncName === CROSS_DOMAIN_OPTION || syncName === CROSS_DOMAIN_DIRECT_OPTION;
}

const generateSettings = ({
  stagedIdentitySyncsData,
  stagedIntegrationsData,
  data,
  consentCategories,
  crossDomainSettings,
}) => {
  const syncInjectorSettings = [];
  let identitySyncs = [...stagedIdentitySyncsData];
  if (data.crossDomain) {
    identitySyncs.push({
      friendlyName: 'Cross Domain',
      name: 'crossDomain',
    });
  }

  if (data.crossDomainDirect) {
    identitySyncs.push({
      friendlyName: 'Cross Domain Direct',
      name: 'crossDomainDirect',
    });
  }

  let categoryCode = null;
  let categoryId = null;
  // customIdentitySync dropdown is only used to add the real syncs, however, it itself is not a sync
  identitySyncs = identitySyncs.filter(item => item.name !== CUSTOM_IDENTITY_SYNC.REQUEST_NAME);
  identitySyncs?.forEach(sync => {
    categoryId = data[`sync-${sync.name}-consentCategoryId`];
    // Only try to find the category if the category id exists.
    // And throw an error if you can't find it.

    if (categoryId) {
      const category = consentCategories.find(category => category.id === categoryId);
      if (!category) {
        throw new Error('Cannot find consent category');
      }
      categoryCode = category.code;
    }

    let tcfEnabled = false;

    // TCF can only be enabled with explicit consent
    if (
      data.impliedConsent === 'false' &&
      data[`sync-${sync.name}-tcfEnabled`] &&
      data[`sync-${sync.name}-tcfEnabled`] === 'true'
    ) {
      tcfEnabled = true;
    }

    let selectedSyncSettings = {};
    if (isCrossDomainSync(sync.name)) {
      if (sync.name === CROSS_DOMAIN_OPTION) {
        const syncUrls = data.crossDomain.split(',').map(url => url.trim());
        selectedSyncSettings = {
          cookieTimeToLive: crossDomainSettings?.cookieTimeToLive || 365,
          timeout: DEFAULT_CROSS_DOMAIN_TIMEOUT,
          syncURLs: syncUrls,
        };
      } else if (sync.name === CROSS_DOMAIN_DIRECT_OPTION) {
        const syncUrl = data.crossDomainDirect.trim();
        selectedSyncSettings = {
          cookieTimeToLive: 365, // default to 1 year, not configurable via the UI
          timeout: DEFAULT_CROSS_DOMAIN_TIMEOUT,
          syncURL: syncUrl,
        };
      }
    } else {
      sync.fields.forEach(field => {
        if (data[`sync-${sync.name}-${field.name}`] !== undefined) {
          let formValue = data[`sync-${sync.name}-${field.name}`];
          if (field.type === 'TEXT_AREA') {
            try {
              if (_isEmpty(formValue)) {
                return;
              } else {
                formValue = JSON.parse(formValue);
              }
            } catch (e) {
              throw new Error(`Error in Identity Syncs: ${sync.name}, field ${field.name} is invalid JSON`);
            }
          } else if (field.type === 'BOOL') {
            formValue = formValue === 'true';
          } else if (field.type === 'NUMBER') {
            formValue = parseInt(formValue);
          }
          selectedSyncSettings[field.name] = formValue;
        }
      });
    }

    const syncData = {
      name: sync.name,
      friendlyName: sync.friendlyName,
      settings: selectedSyncSettings,

      // IDENT. SYNC CONSENT SETTINGS
      // the consentSettings obj will be added to each SyncInjector when building the actual AJS file
      // it will NOT be stored in the DB
      consentSettings: {
        code: categoryCode,
        tcfEnabled,
      },

      // these two consent settings will be stored in the DB and are used to populate the AJS builder form
      consentCategoryId: categoryId,
      tcfEnabled,
    };

    syncInjectorSettings.push(syncData);
  });

  const integrationSettings = [];
  stagedIntegrationsData?.forEach(integration => {
    const selectedIntegrationSettings = {};
    const setSelectedFieldValue = (field, val) => {
      const { type } = field;
      if (type === 'BOOL') {
        return val === 'true' ? true : false;
      }
      return val;
    };
    integration.fields.forEach(field => {
      const selectedField = data[`integration-${integration.name}-${field.name}`];
      let selectedFieldValue = setSelectedFieldValue(field, selectedField);
      if (field.type === 'TEXT_AREA') {
        try {
          if (_isEmpty(selectedField)) {
            selectedFieldValue = field.defaultValue;
          } else {
            selectedFieldValue = JSON.parse(selectedFieldValue);
          }
        } catch (e) {
          throw new Error(`${integration.name} has invalid JSON in field: ${field.name}`);
        }
      } else if (field.type === 'NUMBER') {
        selectedFieldValue = parseInt(field.defaultValue, 10);
      }
      selectedIntegrationSettings[field.name] = selectedFieldValue;
    });
    const integrationData = {
      name: integration.name,
      friendlyName: integration.friendlyName,
      options: selectedIntegrationSettings,
    };
    integrationSettings.push(integrationData);
  });

  return {
    syncInjectorSettings: syncInjectorSettings,
    integrationSettings: integrationSettings,
  };
};

export default generateSettings;
